import { useMemo } from "react";
import { useLocation, useMatch } from "react-router";

export interface AgentRoute {
  page: string;
  projectId?: string;
  predictorId?: string;
  designSpaceId?: string;
  workspaceId?: string;
  executionId?: string;
}

export function useAgentRoute(): {
  defaultAgent: string;
  context: AgentRoute;
} {
  const { pathname } = useLocation();
  const projectMatch = useMatch("/projects/:projectId/*");
  const predictorMatch = useMatch(
    "/projects/:projectId/predictors/:predictorId/*",
  );
  const designSpaceMatch = useMatch(
    "/projects/:projectId/search-spaces/:designSpaceId/*",
  );
  const candidatesMatch = useMatch(
    "/projects/:projectId/workspaces/:workspaceId/executions/:executionId/candidates/*",
  );
  const workspaceMatch = useMatch(
    "/projects/:projectId/workspaces/:workspaceId/*",
  );
  const dataManagerMatch = useMatch("/data-manager/*");

  return useMemo(() => {
    // Most-specific match first: each row maps a route shape to its default
    // agent and page label.
    const routeTable = [
      {
        matched: Boolean(candidatesMatch),
        agent: "candidate-analyst",
        page: "candidates",
      },
      {
        matched: Boolean(dataManagerMatch),
        agent: "data-analyst",
        page: "data-manager",
      },
      {
        matched: pathname === "/projects",
        agent: "oneshot",
        page: "projects-list",
      },
      {
        matched: Boolean(projectMatch),
        agent: "candidate-analyst",
        page: "project",
      },
    ];
    const match = routeTable.find((entry) => entry.matched);
    const defaultAgent: string = match?.agent ?? "oneshot";
    const page = match?.page ?? "other";

    const context: AgentRoute = {
      page,
      projectId: projectMatch?.params.projectId,
      predictorId: predictorMatch?.params.predictorId,
      designSpaceId: designSpaceMatch?.params.designSpaceId,
      workspaceId:
        candidatesMatch?.params.workspaceId ??
        workspaceMatch?.params.workspaceId,
      executionId: candidatesMatch?.params.executionId,
    };

    return { defaultAgent, context };
  }, [
    candidatesMatch,
    dataManagerMatch,
    designSpaceMatch,
    pathname,
    predictorMatch,
    projectMatch,
    workspaceMatch,
  ]);
}
